import { useLocalStorage } from "usehooks-ts";
import type { TrackingConsentObject } from "./tracking-consent.type";
import { useCallback, useMemo } from "react";
import posthog from "posthog-js";

const NO_CONSENT_TIMEOUT_MS = 30 * 24 * 60 * 60 * 1000; // 1 month

export const useTrackingConsent = () => {
    const [trackingConsent, setTrackingConsent] = useLocalStorage<TrackingConsentObject | null>(
        "tracking-consent",
        null,
    );

    const now = Date.now();

    const giveConsent = useCallback(() => {
        setTrackingConsent({ hasConsented: true, lastUpdated: Date.now() });
        posthog.opt_in_capturing();
    }, [setTrackingConsent]);

    const withdrawConsent = useCallback(() => {
        setTrackingConsent({ hasConsented: false, lastUpdated: Date.now() });
        posthog.opt_out_capturing();
    }, [setTrackingConsent]);

    const canTrack = trackingConsent?.hasConsented === true;

    const hasNoConsentRecord = trackingConsent === null;
    const hasWithdrawnConsent = trackingConsent?.hasConsented === false;
    const consentExpired = trackingConsent !== null && now - trackingConsent.lastUpdated > NO_CONSENT_TIMEOUT_MS;
    const shouldAskForConsent = hasNoConsentRecord || (hasWithdrawnConsent && consentExpired);

    return useMemo(
        () => ({
            canTrack,
            giveConsent,
            withdrawConsent,
            shouldAskForConsent,
        }),
        [canTrack, giveConsent, withdrawConsent, shouldAskForConsent],
    );
};
